/*
 * A brand's colours sent to Artwork (cross-app pages, 29 Oct 2026: Brands › a colour block › Open in Artwork). Artwork
 * keeps no palette of its own — a file's colours are the ones it paints with (artwork-core's snapshot `swatches` are a
 * walk over the fills, nothing stores a swatch) — so the colours arrive as an artboard of labelled chips: a filled
 * square per colour, its name and hex under it, on a white page. Once placed, they are the file's colours, listed
 * under "colours in this file" in the colour picker. Read as a layered file, so it lands the way a layered picture
 * does: a new artboard in a new file or one of yours (LayersImport).
 */

export const PALETTE_PAGE = { w: 1080, margin: 80, gap: 32, cols: 4, most: 48 } as const;
const TITLE = { size: 48, weight: 700 } as const;
const NAME = { size: 28, weight: 600 } as const;
const HEX = { size: 22, weight: 500 } as const;

export interface PaletteColour { name: string; hex: string }

const SHORT = /^#?([0-9a-f])([0-9a-f])([0-9a-f])$/i;
const LONG = /^#?([0-9a-f]{6})$/i;

/** "#ABC" / "aabbcc" → "#aabbcc"; anything else → null. */
export function hexOf(value: unknown): string | null {
  const s = String(value ?? '').trim();
  const short = SHORT.exec(s);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase();
  const long = LONG.exec(s);
  return long ? `#${long[1]!.toLowerCase()}` : null;
}

/** The colours a `brand-assets` parcel carries (`payload.colors`), each once, at most PALETTE_PAGE.most. */
export function paletteColours(parcel: { payload?: unknown }): PaletteColour[] {
  const given = (parcel?.payload as { colors?: unknown } | null | undefined)?.colors;
  const out: PaletteColour[] = [];
  for (const c of Array.isArray(given) ? given : []) {
    const hex = hexOf((c as { hex?: unknown })?.hex);
    if (!hex) continue;
    const name = String((c as { name?: unknown })?.name ?? '').trim().slice(0, 80);
    if (out.some((o) => o.hex === hex && o.name === name)) continue;
    out.push({ name, hex });
    if (out.length >= PALETTE_PAGE.most) break;
  }
  return out;
}

const text = (id: string, words: string, x: number, y: number, w: number, font: { size: number; weight: number }, color: string, z: number) => ({
  id,
  kind: 'text',
  name: words.slice(0, 200),
  bbox: { x, y, w, h: Math.ceil(font.size * 1.25) },
  z,
  text: words,
  style: { fontFamily: 'Inter', fontWeight: font.weight, fontSizePx: font.size, color, letterSpacingPx: 0, lineHeight: 1.25, align: 'left', alignY: 'top' },
});

/** The flobi.layers file for a palette parcel (its raw JSON), or null when it holds no colours. */
export function paletteLayerFile(parcel: { name?: unknown; payload?: unknown }): Record<string, unknown> | null {
  const colours = paletteColours(parcel);
  if (!colours.length) return null;
  const name = String(parcel?.name || '').trim() || 'Colours';
  const { w, margin, gap } = PALETTE_PAGE;
  // A chip is always a quarter row wide, so one colour is not a 920 px square; fewer than four sit from the left.
  const cols = PALETTE_PAGE.cols;
  const chip = Math.floor((w - margin * 2 - gap * (cols - 1)) / cols);
  const labels = 16 + Math.ceil(NAME.size * 1.25) + 4 + Math.ceil(HEX.size * 1.25);
  const row = chip + labels + gap * 1.5;
  const top = margin + Math.ceil(TITLE.size * 1.25) + gap;
  const layers: Record<string, unknown>[] = [text('title', name, margin, margin, w - margin * 2, TITLE, '#111111', 1)];
  colours.forEach((c, i) => {
    const x = margin + (i % cols) * (chip + gap);
    const y = Math.round(top + Math.floor(i / cols) * row);
    const label = c.name || c.hex.toUpperCase();
    layers.push({ id: `chip-${i + 1}`, kind: 'fill', name: label, fill: c.hex, bbox: { x, y, w: chip, h: chip }, z: layers.length + 1 });
    if (c.name) layers.push(text(`name-${i + 1}`, c.name, x, y + chip + 16, chip, NAME, '#111111', layers.length + 1));
    layers.push(text(`hex-${i + 1}`, c.hex.toUpperCase(), x, y + chip + 16 + (c.name ? Math.ceil(NAME.size * 1.25) + 4 : 0), chip, HEX, '#6b6b6b', layers.length + 1));
  });
  const rows = Math.ceil(colours.length / cols);
  const h = Math.ceil(top + rows * row - gap * 1.5 + margin);
  layers.unshift({ id: 'page', kind: 'fill', name: 'Page', fill: '#ffffff', bbox: { x: 0, y: 0, w, h }, z: 0 });
  return { kind: 'flobi.layers', version: 1, name, model: 'layers', editableText: true, canvas: { w, h }, size: { width: w, height: h }, fonts: [], layers, pictures: [], preview: null };
}
